import { useCallback, useEffect, useRef, useState } from "react";
import { FiPause, FiPlay, FiRepeat, FiTrash2 } from "react-icons/fi";

type PhonicsAudioLabel = "card" | "sound";

interface Voice {
  id: string;
  phonemeId: string;
  voiceVariant: number;
  label: PhonicsAudioLabel;
  filename: string;
  url: string;
  duration?: number | null;
}

interface PhonicsAudioCardProps {
  voice: Voice;
  isPlaying: boolean;
  onTogglePlay: () => void;
  onDelete: () => void;
  onLabelChange?: (newLabel: PhonicsAudioLabel) => void;
  isChangingLabel?: boolean;
}

// Format seconds to MM:SS
const formatTime = (seconds: number): string => {
  if (!Number.isFinite(seconds)) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

export function PhonicsAudioCard({
  voice,
  isPlaying,
  onTogglePlay,
  onDelete,
  onLabelChange,
  isChangingLabel = false,
}: PhonicsAudioCardProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.play();
    } else {
      audioRef.current.pause();
    }
  }, [isPlaying]);

  const handleAudioEnded = () => {
    setCurrentTime(0);
    onTogglePlay();
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current || isDragging) return;
    setCurrentTime(audioRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (!audioRef.current) return;
    setDuration(audioRef.current.duration);
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleMouseDown = () => {
    setIsDragging(true);
  };

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mouseup", handleMouseUp);
      return () => window.removeEventListener("mouseup", handleMouseUp);
    }
    return undefined;
  }, [isDragging, handleMouseUp]);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={onTogglePlay}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors flex-shrink-0"
          >
            {isPlaying ? <FiPause size={16} /> : <FiPlay size={16} />}
          </button>

          {/* biome-ignore lint/a11y/useMediaCaption: phonics audio is pronunciation teaching, doesn't need a caption */}
          <audio
            ref={audioRef}
            src={voice.url}
            onEnded={handleAudioEnded}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
          />

          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900">Voice {voice.voiceVariant}</span>
              <button
                type="button"
                title={
                  onLabelChange
                    ? `Click to change to ${voice.label === "sound" ? "card" : "sound only"}`
                    : undefined
                }
                onClick={
                  onLabelChange
                    ? () => onLabelChange(voice.label === "sound" ? "card" : "sound")
                    : undefined
                }
                disabled={isChangingLabel}
                className={`flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium transition-colors ${
                  voice.label === "sound"
                    ? "bg-green-100 text-green-700"
                    : "bg-gray-100 text-gray-600"
                } ${onLabelChange ? "cursor-pointer hover:opacity-75" : "cursor-default"} disabled:opacity-50`}
              >
                {voice.label === "sound" ? "sound only" : "card"}
                {onLabelChange && (
                  <FiRepeat size={10} className={isChangingLabel ? "animate-spin" : ""} />
                )}
              </button>
            </div>
            <div className="text-sm text-gray-500 truncate">{voice.filename}</div>
          </div>
        </div>

        <button
          type="button"
          onClick={onDelete}
          className="rounded-md p-2 text-red-600 hover:bg-red-50 transition-colors flex-shrink-0"
          title="Delete"
        >
          <FiTrash2 size={16} />
        </button>
      </div>

      {/* Progress Bar */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500 font-mono w-10 text-right">
          {formatTime(currentTime)}
        </span>

        {/* Seekable Progress Bar */}
        <div
          className="flex-1 h-2 bg-gray-200 rounded-full cursor-pointer relative group"
          onClick={handleSeek}
          onMouseDown={handleMouseDown}
        >
          {/* Progress Fill */}
          <div
            className="absolute top-0 left-0 h-full bg-blue-600 rounded-full transition-all"
            style={{ width: `${progress}%` }}
          />

          {/* Hover Effect */}
          <div className="absolute inset-0 bg-blue-400 opacity-0 group-hover:opacity-20 rounded-full transition-opacity" />

          {/* Scrubber Dot */}
          <div
            className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white border-2 border-blue-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ left: `calc(${progress}% - 6px)` }}
          />
        </div>

        <span className="text-xs text-gray-500 font-mono w-10">{formatTime(duration)}</span>
      </div>
    </div>
  );
}
